<script setup lang="ts">
import { Check, CircleAlert, Download, FileText, Play } from '@lucide/vue'
import { computed, ref, toRef } from 'vue'

import { Button } from '@/components/ui/button'
import { ImageLightbox } from '@ja/core-ui/composites/image-lightbox/index'

import { useCommunicationFormatters } from '../../composables/useCommunicationFormatters'
import { useCommunicationTranslations } from '../../composables/useCommunicationTranslations'
import { useFileTracking } from '../../composables/useFileTracking'
import type { CommunicationAttachment } from '../../types/communication'

import PdfPageViewer from './PdfPageViewer.vue'

const props = withDefaults(
  defineProps<{
    attachments: CommunicationAttachment[]
    /** Communication ID for file interaction tracking. */
    communicationId?: string
    /** When true, file interactions are tracked (for signature-required communications). */
    requiresSignature?: boolean
  }>(),
  {
    communicationId: '',
    requiresSignature: false,
  },
)

const { formatFileSize } = useCommunicationFormatters()
const { tm } = useCommunicationTranslations('communication')

// ============================================================================
// File tracking
// ============================================================================

const { allFilesViewed, isFileViewed, trackOpen, trackClose, trackDownload, trackPageView } = useFileTracking(
  toRef(() => props.communicationId),
  toRef(() => props.requiresSignature),
  toRef(() => props.attachments),
)

defineExpose({ allFilesViewed })

/** Whether to show per-file tracking badges. */
const showTrackingBadges = computed(() => props.requiresSignature && props.communicationId !== '')

// ============================================================================
// Attachment categorization
// ============================================================================

function isPdfMime(mime: string): boolean {
  return mime === 'application/pdf' || mime === 'application/x-pdf'
}

const imageAttachments = computed(() =>
  props.attachments.filter((a) => a.mime_type.startsWith('image/')),
)
const videoAttachments = computed(() =>
  props.requiresSignature ? [] : props.attachments.filter((a) => a.mime_type.startsWith('video/')),
)
/** PDFs with a raster thumbnail (via the `?thumb=1` proxy endpoint). */
const previewablePdfAttachments = computed(() =>
  props.requiresSignature
    ? []
    : props.attachments.filter(
      (a) =>
        isPdfMime(a.mime_type)
        && a.thumb_url !== null
        && a.thumb_url !== '',
    ),
)
const otherAttachments = computed(() =>
  props.attachments.filter(
    (a) => {
      if (a.mime_type.startsWith('image/')) {
        return false
      }
      // When signature required, all non-image files go to the download list
      if (props.requiresSignature) {
        return true
      }
      if (a.mime_type.startsWith('video/')) {
        return false
      }
      if (isPdfMime(a.mime_type) && a.thumb_url !== null && a.thumb_url !== '') {
        return false
      }

      return true
    },
  ),
)

// ============================================================================
// Lightbox (images + videos)
// ============================================================================

const lightboxOpen = ref(false)
const lightboxSrc = ref('')
const lightboxAlt = ref('')
const lightboxMediaType = ref<'image' | 'video'>('image')
const lightboxDownloadHref = ref<string | undefined>(undefined)
const lightboxAttachment = ref<CommunicationAttachment | null>(null)

function openLightbox(
  attachment: CommunicationAttachment,
  type: 'image' | 'video' = 'image',
  previewSrc?: string,
) {
  lightboxSrc.value = previewSrc ?? attachment.preview_url ?? attachment.url
  lightboxAlt.value = attachment.name
  lightboxMediaType.value = type
  lightboxDownloadHref.value = `${attachment.url}?download=1`
  lightboxAttachment.value = attachment
  lightboxOpen.value = true
  trackOpen(attachment)
}

function onLightboxClose(open: boolean) {
  if (!open && lightboxAttachment.value) {
    trackClose(lightboxAttachment.value)
    lightboxAttachment.value = null
  }
  lightboxOpen.value = open
}

// ============================================================================
// PDF Page Viewer
// ============================================================================

const pdfViewerOpen = ref(false)
const pdfViewerAttachment = ref<CommunicationAttachment | null>(null)

function openPdfViewer(attachment: CommunicationAttachment) {
  pdfViewerAttachment.value = attachment
  pdfViewerOpen.value = true
}

function onPdfViewerClose(open: boolean) {
  pdfViewerOpen.value = open
  if (!open) {
    pdfViewerAttachment.value = null
  }
}

function onPdfFileOpened(attachment: CommunicationAttachment) {
  trackOpen(attachment)
}

function onPdfFileClosed(attachment: CommunicationAttachment) {
  trackClose(attachment)
}

function onPdfPageViewed(attachment: CommunicationAttachment, page: number) {
  trackPageView(attachment, page)
}

// ============================================================================
// Download tracking
// ============================================================================

function handleDownloadClick(event: Event, attachment: CommunicationAttachment) {
  event.preventDefault()
  trackOpen(attachment)
  trackDownload(attachment)
  window.open(attachment.url, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <div v-if="attachments.length > 0" class="space-y-3">
    <h3 class="text-sm font-medium text-foreground">
      {{ tm('detail.attachments', { count: attachments.length }) }}
    </h3>

    <!-- Tracking status bar (only when signature required and not all viewed) -->
    <div
      v-if="showTrackingBadges && !allFilesViewed"
      class="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300"
    >
      <CircleAlert class="size-4 shrink-0" />
      <span>{{ tm('detail.files_review_required') }}</span>
    </div>

    <!-- Image Gallery Grid -->
    <div v-if="imageAttachments.length > 0" class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      <button
        v-for="attachment in imageAttachments"
        :key="attachment.url"
        type="button"
        class="group relative aspect-square overflow-hidden rounded-lg border bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        :class="showTrackingBadges
          ? (isFileViewed(attachment) ? 'border-green-300 dark:border-green-700' : 'border-amber-300 ring-1 ring-amber-200 dark:border-amber-700 dark:ring-amber-800')
          : 'border-border'"
        @click="openLightbox(attachment)"
      >
        <img
          :src="attachment.thumb_url ?? attachment.url"
          :alt="attachment.name"
          class="size-full object-cover transition-transform group-hover:scale-105"
          loading="lazy"
        />
        <!-- Viewed / Not viewed badge -->
        <div
          v-if="showTrackingBadges"
          class="absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full shadow-sm"
          :class="isFileViewed(attachment)
            ? 'bg-green-500 text-white'
            : 'bg-amber-400 text-amber-900'"
        >
          <Check v-if="isFileViewed(attachment)" class="size-3.5" />
          <CircleAlert v-else class="size-3.5" />
        </div>
        <div class="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 opacity-0 transition-opacity group-hover:opacity-100">
          <span class="truncate text-xs text-white">{{ attachment.name }}</span>
        </div>
      </button>
    </div>

    <!-- PDF previews (opens multi-page viewer when pdf_pages_url is available, otherwise lightbox) -->
    <div v-if="previewablePdfAttachments.length > 0" class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      <button
        v-for="attachment in previewablePdfAttachments"
        :key="attachment.url"
        type="button"
        class="group relative aspect-[3/4] overflow-hidden rounded-lg border bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        :class="showTrackingBadges
          ? (isFileViewed(attachment) ? 'border-green-300 dark:border-green-700' : 'border-amber-300 ring-1 ring-amber-200 dark:border-amber-700 dark:ring-amber-800')
          : 'border-border'"
        @click="attachment.pdf_pages_url
          ? openPdfViewer(attachment)
          : openLightbox(attachment, 'image', attachment.preview_url ?? attachment.thumb_url ?? undefined)"
      >
        <img
          :src="attachment.thumb_url ?? attachment.url"
          :alt="attachment.name"
          class="size-full object-cover transition-transform group-hover:scale-105"
          loading="lazy"
        />
        <!-- Viewed / Not viewed badge -->
        <div
          v-if="showTrackingBadges"
          class="absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full shadow-sm"
          :class="isFileViewed(attachment)
            ? 'bg-green-500 text-white'
            : 'bg-amber-400 text-amber-900'"
        >
          <Check v-if="isFileViewed(attachment)" class="size-3.5" />
          <CircleAlert v-else class="size-3.5" />
        </div>
        <div class="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 text-left">
          <span class="line-clamp-2 text-xs text-white">{{ attachment.name }}</span>
          <span v-if="attachment.size" class="mt-0.5 block text-[10px] text-white/80">
            {{ formatFileSize(attachment.size) }}
          </span>
        </div>
      </button>
    </div>

    <!-- Video Grid -->
    <div v-if="videoAttachments.length > 0" class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      <button
        v-for="attachment in videoAttachments"
        :key="attachment.url"
        type="button"
        class="group relative aspect-video overflow-hidden rounded-lg border bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        :class="showTrackingBadges
          ? (isFileViewed(attachment) ? 'border-green-300 dark:border-green-700' : 'border-amber-300 ring-1 ring-amber-200 dark:border-amber-700 dark:ring-amber-800')
          : 'border-border'"
        @click="openLightbox(attachment, 'video')"
      >
        <div class="absolute inset-0 flex items-center justify-center">
          <div class="flex size-10 items-center justify-center rounded-full bg-black/50 shadow-lg transition-all group-hover:scale-110 group-hover:bg-black/70">
            <Play class="ml-0.5 size-5 text-white" />
          </div>
        </div>
        <!-- Viewed / Not viewed badge -->
        <div
          v-if="showTrackingBadges"
          class="absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full shadow-sm"
          :class="isFileViewed(attachment)
            ? 'bg-green-500 text-white'
            : 'bg-amber-400 text-amber-900'"
        >
          <Check v-if="isFileViewed(attachment)" class="size-3.5" />
          <CircleAlert v-else class="size-3.5" />
        </div>
        <div class="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 opacity-0 transition-opacity group-hover:opacity-100">
          <span class="truncate text-xs text-white">{{ attachment.name }}</span>
        </div>
      </button>
    </div>

    <!-- Other Files List -->
    <div v-if="otherAttachments.length > 0" class="grid gap-2">
      <a
        v-for="attachment in otherAttachments"
        :key="attachment.url"
        :href="attachment.url"
        target="_blank"
        rel="noopener noreferrer"
        class="group flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent"
        :class="showTrackingBadges
          ? (isFileViewed(attachment)
            ? 'border-green-200 bg-green-50/50 dark:border-green-800/50 dark:bg-green-950/20'
            : 'border-amber-200 bg-amber-50/50 dark:border-amber-800/50 dark:bg-amber-950/20')
          : 'border-border bg-card'"
        @click="handleDownloadClick($event, attachment)"
      >
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-lg"
          :class="showTrackingBadges
            ? (isFileViewed(attachment) ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30')
            : 'bg-muted'"
        >
          <Check
            v-if="showTrackingBadges && isFileViewed(attachment)"
            class="size-5 text-green-600 dark:text-green-400"
          />
          <CircleAlert
            v-else-if="showTrackingBadges && !isFileViewed(attachment)"
            class="size-5 text-amber-600 dark:text-amber-400"
          />
          <FileText v-else class="size-5 text-muted-foreground" />
        </div>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium text-foreground">{{ attachment.name }}</p>
          <p class="text-xs text-muted-foreground">
            <span v-if="attachment.size">{{ formatFileSize(attachment.size) }}</span>
            <template v-if="showTrackingBadges">
              <span v-if="attachment.size"> · </span>
              <span
                :class="isFileViewed(attachment) ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'"
              >
                {{ isFileViewed(attachment) ? tm('detail.file_viewed') : tm('detail.file_not_viewed') }}
              </span>
            </template>
          </p>
        </div>
        <Button variant="ghost" size="sm" as="span" class="shrink-0 opacity-0 transition-opacity group-hover:opacity-100">
          <Download class="size-4" />
          {{ tm('detail.download') }}
        </Button>
      </a>
    </div>

    <!-- Lightbox (images + videos) -->
    <ImageLightbox
      :open="lightboxOpen"
      :src="lightboxSrc"
      :alt="lightboxAlt"
      :media-type="lightboxMediaType"
      :title="lightboxAlt"
      :download-href="lightboxDownloadHref"
      :download-label="tm('detail.download')"
      @update:open="onLightboxClose"
    />

    <!-- PDF Page Viewer -->
    <PdfPageViewer
      :open="pdfViewerOpen"
      :attachment="pdfViewerAttachment"
      :download-label="tm('detail.download')"
      @update:open="onPdfViewerClose"
      @file-opened="onPdfFileOpened"
      @file-closed="onPdfFileClosed"
      @page-viewed="onPdfPageViewed"
    />
  </div>
</template>
